import { skipToken } from '@reduxjs/toolkit/query';

import { useGetMomentsSessionReadByLegacyIdQuery } from '@/store/api/momentsApi';

interface UseSessionNotTranscribedArg {
  legacySessionId: string | undefined;
  skip?: boolean;
}

/**
 * Whether a past session was not transcribed, for the "· Not transcribed"
 * subhead label in the Figma "Past Sessions" frame (17420:41868).
 *
 * Absence of a reflection artifact cannot answer this on its own: a session that
 * was transcribed but whose reflection has not been generated yet also has no
 * artifact. The Moments per-session read answers it: a session with no
 * transcript file was never recorded, and one whose readiness reason is
 * `not_transcribed` has only Gemini notes (MEN-5403). A no-show
 * (`no_conversation`) is not labelled, because it was transcribed. The session
 * detail page reads the same endpoint, so the cache is shared.
 *
 * Deliberately false while the request is in flight or failed, so a card never
 * flashes "Not transcribed" at a session that simply has not answered yet.
 */
export const useSessionNotTranscribed = ({
  legacySessionId,
  skip = false,
}: UseSessionNotTranscribedArg): boolean => {
  const { currentData, isSuccess } = useGetMomentsSessionReadByLegacyIdQuery(
    skip || !legacySessionId ? skipToken : { legacySessionId },
  );
  const item = currentData?.item;
  if (!isSuccess || !item) {
    return false;
  }

  return (
    !item.attachments.some((attachment) => attachment.kind === 'transcript') ||
    item.artifactGenerationReadiness?.reason === 'not_transcribed'
  );
};
